// lib/source-usage.ts
import type { KernelState, SourceUsageInterval } from '#types/types';
import { canAccessSource } from '#lib/access/sourceAccess';
import { SOURCE_DOMAIN_ENTITY_TYPES } from '#lib/source-defaults';
import { isTableFocusSubject, selectActiveSubjectFromInventoryState } from '#lib/leasing/session-focus';
import { canAccessAnalyst } from '#lib/access/analystAccess';

export const SOURCE_USAGE_EVENT = 'SOURCE_USAGE_INTERVAL';
export const SOURCE_USAGE_IDLE_MS = 60_000;
export const SOURCE_USAGE_FLUSH_MS = 30_000;
export const SOURCE_USAGE_MAX_GAP_MS = 45_000;

export const isSourceUsageRecord = (id: unknown, eventType: unknown): boolean =>
  (typeof id === 'string' && id.startsWith('source-usage:')) ||
  (typeof eventType === 'string' && eventType.startsWith('SOURCE_USAGE_'));

export function sourceUsageSurface(state: KernelState, analystOpen = false): boolean {
  if (!canAccessSource(state.userContext) || state.userContext.isImpersonating || state.portalStandaloneView) return false;
  const session = state.activeChatSessionId ? state.inventory.ChatSession.registry[state.activeChatSessionId] : null;
  if (session && ['studio', 'slides', 'analyst', 'doctrine', 'master'].includes(session.creationModule ?? '')) return false;
  const subject = selectActiveSubjectFromInventoryState(state);
  if (subject?.entityType === 'Project' || subject?.entityType === 'Organisation') return false;
  if (analystOpen && canAccessAnalyst(state.userContext) && (!subject || isTableFocusSubject(subject))) return false;
  if (subject && isTableFocusSubject(subject)) return SOURCE_DOMAIN_ENTITY_TYPES.has(subject.entityType);
  return Boolean(session?.creationModule === 'source' || (subject && SOURCE_DOMAIN_ENTITY_TYPES.has(subject.entityType)));
}

/** Union, not sum: two visible windows never become twice the user's elapsed time. */
export function unionSourceUsageMs(segments: [number, number][]): number {
  const sorted = segments.filter(([a, b]) => Number.isFinite(a) && Number.isFinite(b) && b > a).sort((a, b) => a[0] - b[0]);
  let total = 0, end = -Infinity;
  for (const [a, b] of sorted) {
    total += Math.max(0, b - Math.max(a, end));
    end = Math.max(end, b);
  }
  return total;
}

/** Monotonic samples are mapped to one wall-clock anchor; client clock changes cannot add time. */
export function createSourceUsageClock(visitId: string, wall: number, monotonic: number) {
  let last = monotonic, from = wall, lastInput = -Infinity, visible = false, focused = false, gapMs = 0;
  let visibleSegments: [number, number][] = [], activeSegments: [number, number][] = [];
  const at = (value: number) => Math.round(wall + value - monotonic);
  const append = (segments: [number, number][], a: number, b: number) => {
    if (b <= a) return false;
    const tail = segments.at(-1);
    if (tail && tail[1] === a) tail[1] = b;
    else if (segments.length < 120) segments.push([a, b]);
    else { gapMs += b - a; return false; }
    return true;
  };
  return {
    visitId,
    sample(now: number, nextVisible: boolean, nextFocused: boolean, input = false) {
      const elapsed = now - last;
      if (elapsed > SOURCE_USAGE_MAX_GAP_MS) {
        gapMs += elapsed;
        lastInput = -Infinity;
      } else if (elapsed >= 0 && visible) {
        const recorded = append(visibleSegments, at(last), at(now));
        if (focused && recorded) append(activeSegments, at(last), at(Math.min(now, lastInput + SOURCE_USAGE_IDLE_MS)));
      }
      last = Math.max(last, now);
      visible = nextVisible;
      focused = nextFocused;
      if (input && visible && focused) lastInput = last;
    },
    take(id: string, droppedRecords = 0): SourceUsageInterval {
      const result = { id, visitId, from, to: at(last), visible: visibleSegments, active: activeSegments, gapMs: Math.round(gapMs), droppedRecords };
      from = at(last); visibleSegments = []; activeSegments = []; gapMs = 0;
      return result;
    },
  };
}
